import { type FC, type JSX, type ReactNode } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'

import { type OfferShippingsV1 } from '@redteclab/api/clients/bully'

import { Link } from 'base-ui'

import { useOpenableControls } from '../../../../../generic-hooks'
import { productAccessorIsSaeOffer } from '../../../../model/productAccessor/productAccessorIsSaeOffer'
import { productAccessorStockStatusIsAvailable } from '../../../../model/productAccessor/productAccessorStockStatusIsAvailable'
import { shippingAccessorIsFreeShippingMarketplace } from '../../../../model/shippingAccessor/shippingAccessorIsFreeShippingMarketplace'
import { ProductDeliveryInfoDialog } from '../../../ProductDeliveryInfoDialog'
import { useProductPageContext } from '../../../productPageContext'
import { useProductDeliveryPrices } from '../../hooks/useProductDeliveryPrices'

const determineFreeShipping = (
  isMarketplace: boolean,
  isFreeShippingSae: boolean,
  marketplaceDeliveryCosts: OfferShippingsV1[] | null,
): boolean => {
  return (
    (!isMarketplace && isFreeShippingSae) ||
    (isMarketplace &&
      shippingAccessorIsFreeShippingMarketplace(marketplaceDeliveryCosts))
  )
}

const getAmount = (value: number, currency: string): JSX.Element => (
  <FormattedNumber
    currency={currency}
    key="amount"
    minimumFractionDigits={Number.isSafeInteger(value) ? 0 : undefined}
    style="currency"
    value={value}
  />
)

const freeShippingMessage = (): JSX.Element => {
  return (
    <span className="font-medium text-dark-success">
      <FormattedMessage id="buyBox.link.label.aboveFreeShippingLimit" />
    </span>
  )
}

const onClickModal = (handleOpen: () => void): void => {
  handleOpen()
}

export const ProductBuyBoxDeliveryInfo: FC = () => {
  const {
    currentOffer,
    currentPrescriptionType,
    offerId,
    product,
    productTemplate,
    shipping: { marketplaceDeliveryCosts },
  } = useProductPageContext()

  const { freeShippingThreshold, isFreeShippingSae, shippingPriceMarketplace } =
    useProductDeliveryPrices({
      currentPrescriptionType,
      offerId,
      product,
      productTemplate,
    })

  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)

  if (!currentOffer || !productAccessorStockStatusIsAvailable(product)) {
    return null
  }

  const isMarketplace = !productAccessorIsSaeOffer(currentOffer)

  if (
    determineFreeShipping(
      isMarketplace,
      isFreeShippingSae,
      marketplaceDeliveryCosts,
    )
  ) {
    return freeShippingMessage()
  }

  const linkToModal = (text: ReactNode): JSX.Element => (
    <Link
      color="inherit"
      key="linkToModal"
      onClick={(): void => {
        onClickModal(handleOpen)
      }}
      slots={{ root: 'button' }}
      type="button"
    >
      {text}
    </Link>
  )

  return (
    <span>
      {isMarketplace ? (
        <FormattedMessage
          id="buyBox.link.label.belowFreeShippingLimitWithLinkMp_ho"
          values={{
            amount: getAmount(
              shippingPriceMarketplace ?? 0,
              freeShippingThreshold.currency,
            ),
            linkToModal,
          }}
        />
      ) : (
        // Consolidated condition - only display if amount is a number
        typeof freeShippingThreshold.amount === 'number' && (
          <FormattedMessage
            id="buyBox.link.label.belowFreeShippingLimitWithLink_ho"
            values={{
              amount: getAmount(
                freeShippingThreshold.amount,
                freeShippingThreshold.currency,
              ),
              linkToModal,
            }}
          />
        )
      )}
      <ProductDeliveryInfoDialog isOpen={isOpen} onClose={handleClose} />
    </span>
  )
}
